<template>
  <div>
    <h4 class="table-page-header">
      Counselors
      <el-tooltip content="Add New Counselor">
        <el-button type="text" @click="createCounselor">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
      </div>
    </div>
    <AIVTable
      :columns="counselorColumns"
      :data="counselors"
      :pagination="pagination"
      :item-name="'counselors'"
      @pageChange="fetchCounselors"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-actions="{ row }">
        <button
          class="btn btn-sm btn-warning mr-2"
          @click="editCounselor(row)"
          title="Edit"
        >
          <i class="fa-solid fa-pen-to-square"></i>
        </button>
        <button
          class="btn btn-sm btn-danger"
          @click="$confirmAction(() => deleteCounselor(row))"
          title="Delete"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </template>
    </AIVTable>
    <AIVDialog
      v-model="$modal.state.create_or_edit_counselor"
      :title="
        counselorBeingEdited ? 'Edit Counselor' : 'Create a New Counselor'
      "
      width="90%"
      height="85vh"
      class="counselor-edit-modal"
    >
      <div class="row col-md-12 d-flex justify-content-between">
        <el-form
          ref="createForm"
          :model="counselor"
          label-position="top"
          label-width="120px"
        >
          <AIVRow
            :record.sync="counselor"
            :fields="[
              { label: 'Name', key: 'name' },
              { label: 'Title', key: 'title' },
            ]"
          />
          <AIVRow
            :record.sync="counselor"
            :fields="[
              { label: 'Phone', key: 'phone' },
              { label: 'Email', key: 'email' },
            ]"
          />
          <AIVRow
            :record.sync="counselor"
            :fields="[
              {
                label: 'Office',
                key: 'id_offices',
                type: 'select',
                options: officeOptions,
                placeholder: 'Select Office',
              },
            ]"
          />
        </el-form>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveCounselor">{{
            counselorBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import { counselorsService } from "@/serviceHandlers/counselorsService";
import { officesService } from "@/serviceHandlers/officesService";

export default {
  name: "Counselors",
  data() {
    return {
      counselorBeingEdited: null,
      counselorColumns: [
        { key: "name", label: "Name" },
        { key: "title", label: "Title" },
        { key: "phone", label: "Phone", class: "hide-on-medium" },
        { key: "email", label: "Email", class: "hide-on-medium" },
        { key: "office_name", label: "Office" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newCounselor: {
        name: "",
        title: "",
        phone: "",
        email: "",
        id_offices: null,
      },
      searchQuery: "",
      sortProp: "",
      sortOrder: "",
      counselors: [],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0
      },
      officeOptions: [],
    };
  },
  computed: {
    counselor: {
      get() {
        return this.counselorBeingEdited || this.newCounselor;
      },
      set(val) {
        if (this.counselorBeingEdited) {
          this.counselorBeingEdited = val;
        } else {
          this.newCounselor = val;
        }
      },
    },
  },
  async mounted() {
    await this.fetchCounselors();
    await this.getOffices();
  },
  methods: {
    async fetchCounselors(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await counselorsService.listPaginated(params);
        this.counselors = response.rows;
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load counselors: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchCounselors(1);
    },
    clearSearch() {
      this.searchQuery = '';
      this.pagination.currentPage = 1;
      this.fetchCounselors(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchCounselors(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchCounselors(1);
    },
    getCounselors() {
      return counselorsService
        .list()
        .then((counselors) => {
          this.counselors = counselors.map((counselor) => ({
            ...counselor,
            office_name: counselor.office ? counselor.office.name : "No Office Assigned",
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load counselors: " + error);
        });
    },
    getOffices() {
      return officesService
        .list()
        .then((offices) => {
          this.officeOptions = offices.map((office) => ({
            id: office.id,
            name: office.name,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load offices: " + error);
        });
    },
    deleteCounselor(counselor) {
      return counselorsService
        .delete(counselor.id)
        .then(() => {
          this.$notifySuccess("Successfully deleted counselor!");
          this.fetchCounselors(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError("Error deleting counselor: " + error);
        });
    },
    async saveCounselor() {
      const counselorObj = this.counselor;

      if (!counselorObj.name) {
        this.$notifyError("No counselor name!");
        return;
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.counselorBeingEdited) {
        apiSaveCall = counselorsService
          .update(counselorObj.id, counselorObj)
          .then(() => {
            this.$notifySuccess("Successfully updated counselor.");
          });
      } else {
        apiSaveCall = counselorsService.create(counselorObj).then(() => {
          this.$notifySuccess("Successfully created counselor.");
        });
      }
      apiSaveCall
        .catch((error) => {
          this.$notifyError("Error: " + error);
        })
        .then(() => {
          return this.fetchCounselors();
        })
        .finally(() => {
          this.$spinner.hide();
          this.$modal.close("create_or_edit_counselor");
          this.counselorBeingEdited = null;
        });
    },
    createCounselor() {
      this.counselorBeingEdited = null;
      this.newCounselor = {
        name: "",
        title: "",
        phone: "",
        email: "",
        id_offices: null,
      };
      this.$modal.open("create_or_edit_counselor");
    },
    async editCounselor(counselor) {
      this.counselorBeingEdited = { ...counselor };
      this.$modal.open("create_or_edit_counselor");
    },
  },
};
</script>

<style scoped>
/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}
</style>